if (!customElements.get('basin-form')) {
  class BasinForm extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.form = this.querySelector('[data-basin-form-element]');
      if (!this.form) return;

      this.fields = this.querySelector('[data-basin-form-fields]');
      this.submitButton = this.querySelector('[data-basin-form-submit]');
      this.successMessage = this.querySelector('[data-basin-form-success]');
      this.errorMessage = this.querySelector('[data-basin-form-error]');
      this.recaptchaResponse = this.querySelector('[data-basin-recaptcha-response]');
      this.recaptchaSiteKey = this.dataset.recaptchaSiteKey;
      this.handleSubmit = this.handleSubmit.bind(this);
      this.form.addEventListener('submit', this.handleSubmit);
      this.initialized = true;
    }

    disconnectedCallback() {
      if (this.form && this.handleSubmit) {
        this.form.removeEventListener('submit', this.handleSubmit);
      }

      this.abortController?.abort();
      this.initialized = false;
    }

    async handleSubmit(event) {
      event.preventDefault();

      if (
        this.submitting ||
        !this.form.hasAttribute('action') ||
        !this.form.reportValidity()
      ) {
        return;
      }

      this.submitting = true;
      this.abortController = new AbortController();
      this.setSubmittingState(true);
      this.hideMessage(this.successMessage);
      this.hideMessage(this.errorMessage);

      let submitted = false;

      try {
        await this.setRecaptchaToken();

        const response = await fetch(this.form.action, {
          method: 'POST',
          headers: {
            Accept: 'application/json'
          },
          body: new FormData(this.form),
          signal: this.abortController.signal
        });

        if (!response.ok) throw new Error('Basin submission failed');

        submitted = true;
        this.form.reset();
        if (this.fields) this.fields.hidden = true;
        this.showMessage(this.successMessage);
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) {
          this.showMessage(this.errorMessage);
        }
      } finally {
        this.submitting = false;
        this.abortController = null;
        this.setSubmittingState(false, submitted);
      }
    }

    async setRecaptchaToken() {
      if (!this.recaptchaSiteKey) return;

      if (!this.recaptchaResponse || !window.grecaptcha?.ready) {
        throw new Error('reCAPTCHA failed to load');
      }

      await new Promise((resolve) => window.grecaptcha.ready(resolve));

      const token = await window.grecaptcha.execute(this.recaptchaSiteKey, {
        action: 'submit'
      });

      if (!token) throw new Error('reCAPTCHA verification failed');

      this.recaptchaResponse.value = token;
    }

    setSubmittingState(isSubmitting, submitted = false) {
      if (!this.submitButton) return;

      this.submitButton.disabled = isSubmitting || submitted;

      if (isSubmitting) {
        this.submitButton.setAttribute('aria-busy', 'true');
      } else {
        this.submitButton.removeAttribute('aria-busy');
      }
    }

    hideMessage(message) {
      if (message) message.hidden = true;
    }

    showMessage(message) {
      if (!message) return;

      message.hidden = false;
      message.focus();
    }
  }

  customElements.define('basin-form', BasinForm);
}
